import React from 'react';
import { useDispatch } from 'react-redux';
import { withFormsy } from 'formsy-react';
import { Chip, styled } from '@mui/material';
import Autocomplete, { createFilterOptions } from '@mui/material/Autocomplete';
import { useDeepCompareEffect } from '@fuse/hooks';

import BaseTextField, { type FieldProps } from './components/BaseTextField';
import { executeStored } from '../Utils';
import useSettings from '../hooks/useSettings';
import useUserData from '../hooks/useUserData';
import useCancelablePromise from '../hooks/useCancelablePromise';

type TagsSelectFieldProps = Omit<FieldProps<any[]>, 'startAdornment' | 'endAdornment'>;

const StyledAutocomplete = styled(Autocomplete)(({ theme }) => ({
	'& > .MuiTextField-root > .MuiInputBase-root > .MuiInputAdornment-root': {
		'&.MuiInputAdornment-positionStart': {
			margin: 0,
			paddingLeft: 4,
			paddingRight: 4
		}
	}
}));

/** Component used to display and manage tags */
function TagsSelectField(props: TagsSelectFieldProps) {
	const dispatch = useDispatch();
	const { t } = useSettings();
	const userData = useUserData();
	const getCancelablePromise = useCancelablePromise();

	const hasLogs = props.logProps?.hasLogs(props.name);

	const [options, setOptions] = React.useState();
	const [isLoading, setIsLoading] = React.useState(true);
	const [open, setOpen] = React.useState(false);

	const getOptionSelected = (option, currentValue) => option.tags_title === currentValue.tags_title;

	const filter = createFilterOptions();

	function filterOptions(opts, params) {
		const filtered = filter(opts, params);

		const { inputValue } = params;
		const isExisting = opts.some(option => inputValue.toLowerCase() === option.tags_title.toLowerCase());
		if (inputValue !== '' && !isExisting) {
			filtered.push({
				tags_title: `${t('TAGS_ADD')} "${inputValue}"`,
				newTitle: inputValue
			});
		}

		return filtered;
	}

	function handleChange(event, selectedValue) {
		let newValue = [];
		if (selectedValue) {
			newValue = selectedValue.map(v => ({
				tags_id: v.tags_id,
				tags_title: v.newTitle || v.tags_title,
				tags_user_created_uid: userData.user_uid
			}));
		}

		if (props.beforeChange) props.beforeChange(newValue);
		props.sendChange(props.name, newValue);
		if (props.afterChange) props.afterChange(newValue);
	}

	function handleKeyDown(event) {
		if (event.key === 'Enter') {
			event.preventDefault();
		}
	}

	function renderValue(fieldValue: any | any[], getItemProps) {
		return fieldValue.map((option, index) => {
			const { key, ...itemProps } = getItemProps({ index });
			return (
				<Chip
					{...itemProps}
					key={key}
					label={option.tags_title}
					size="small"
					disabled={props.isEdit && itemProps.disabled}
					onDelete={props.isEdit ? itemProps.onDelete : undefined}
				/>
			);
		});
	}

	useDeepCompareEffect(() => {
		if (!options || open) {
			setIsLoading(true);
			executeStored({
				action: 'st_tags_list',
				params: {},
				dispatch,
				getCancelablePromise,
				successHandler: response => {
					const { list } = response.data;
					setOptions(list || []);
					setIsLoading(false);
				}
			});
		}
	}, [dispatch, open, options, props.isEdit]);

	return (
		<StyledAutocomplete
			className={props.className}
			renderInput={params => (
				<BaseTextField
					{...params}
					name={props.name}
					label={props.label}
					InputLabelProps={{
						...params.InputLabelProps,
						shrink: true
					}}
					InputProps={{ ...params.InputProps }}
					isEdit={props.isEdit}
					required={props.required}
					errorMessage={props.errorMessage}
					isFormSubmitted={props.isFormSubmitted}
					isPristine={props.isPristine}
					isValid={props.isValid}
					logProps={props.logProps}
				/>
			)}
			options={options || []}
			value={props.value || []}
			onChange={handleChange}
			onKeyDown={handleKeyDown}
			disabled={!props.isEdit || hasLogs}
			open={props.isEdit && open}
			onOpen={() => setOpen(true)}
			onClose={() => setOpen(false)}
			loading={isLoading}
			loadingText={t('LOADING')}
			noOptionsText={t('LIST_NO_ITEMS')}
			getOptionLabel={(option: any) => option.tags_title}
			renderValue={renderValue}
			isOptionEqualToValue={getOptionSelected}
			filterOptions={filterOptions}
			multiple
			autoHighlight
			selectOnFocus
			forcePopupIcon={!!props.isEdit}
			disableClearable={!props.isEdit}
			disableCloseOnSelect
		/>
	);
}

export default React.memo(withFormsy(TagsSelectField));
